/**
 * usePosition Hook
 *
 * Requests the user's geolocation via the browser Geolocation API.
 * The request is deferred until `enabled` is `true` (default), so
 * consumers can avoid triggering the browser permission prompt until
 * the user actually needs geolocation (e.g. selecting boutique pickup).
 *
 * Set `watch: true` to continuously track position via `watchPosition`.
 *
 * @example
 * ```tsx
 * const { position, error } = usePosition({ enabled: isBoutique });
 * ```
 */

import { useCallback, useEffect, useState } from 'react';

interface UsePositionOptions {
  enabled?: boolean;
  watch?: boolean;
  settings?: PositionOptions;
}

interface UsePositionResult {
  position: GeolocationPosition | null;
  error: GeolocationPositionError | null;
}

const DEFAULT_SETTINGS: PositionOptions = {
  enableHighAccuracy: false,
  timeout: 10_000,
  maximumAge: 600_000,
};

export function usePosition({
  enabled = true,
  watch = false,
  settings = DEFAULT_SETTINGS,
}: UsePositionOptions = {}): UsePositionResult {
  const [position, setPosition] = useState<GeolocationPosition | null>(null);
  const [error, setError] = useState<GeolocationPositionError | null>(null);

  const onSuccess = useCallback<PositionCallback>((pos) => setPosition(pos), []);
  const onError = useCallback<PositionErrorCallback>((err) => setError(err), []);

  useEffect(() => {
    if (!enabled || !navigator.geolocation) return;

    if (watch) {
      const watcherId = navigator.geolocation.watchPosition(onSuccess, onError, settings);
      return () => navigator.geolocation.clearWatch(watcherId);
    }

    navigator.geolocation.getCurrentPosition(onSuccess, onError, settings);
  }, [enabled, watch, settings, onSuccess, onError]);

  return { position, error };
}
